import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import type { DomainInputStatus } from '../../../contracts';

interface CustomDomainRowProps {
  domain: string;
  status: DomainInputStatus;
  onSelect: (domain: string) => void;
  /**
   * Optional content rendered absolutely on the right side of the row, vertically
   * centered against the `Add {domain}` action button. Used by the multi-select
   * source picker to host its `Select all` / `Clear` controls inline with the
   * custom-domain affordance instead of stacking another header row above it.
   */
  rightSlot?: ReactNode;
}

export const CustomDomainRow = ({
  domain,
  status,
  onSelect,
  rightSlot,
}: CustomDomainRowProps) => {
  const isAddable = status === 'valid';

  return (
    <div className="border-b border-border">
      <div className="relative">
        <button
          type="button"
          data-testid="custom-domain-add-button"
          disabled={!isAddable}
          onMouseDown={(e) => {
            e.preventDefault();
            if (isAddable) onSelect(domain);
          }}
          className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-surface-page disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <div className="flex size-6 shrink-0 items-center justify-center">
            <Plus
              size={16}
              className={isAddable ? 'text-text-link' : 'text-text-disabled'}
            />
          </div>
          <div className="min-w-0 flex-1">
            <TypographyP2 className={isAddable ? 'text-text-link' : 'text-text-disabled'}>
              <FormattedMessage
                defaultMessage="Add {domain}"
                id="gBcjBfkgTp"
                values={{ domain }}
              />
            </TypographyP2>
          </div>
        </button>
        {rightSlot && (
          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {rightSlot}
          </div>
        )}
      </div>
      {status === 'invalid' && (
        <TypographyCaption className="px-3 pb-2 text-validation-destructive">
          <FormattedMessage
            defaultMessage="Enter a valid domain or URL"
            id="Vy15z1ALsw"
          />
        </TypographyCaption>
      )}
      {status === 'blocked' && (
        <TypographyCaption className="px-3 pb-2 text-validation-destructive">
          <FormattedMessage
            defaultMessage="This domain is blocked and cannot be added."
            id="qIV0uwX8kz"
            description="Error shown when a user tries to add a content-safety blocked domain to a source collection"
          />
        </TypographyCaption>
      )}
      {status === 'duplicate' && (
        <TypographyCaption className="px-3 pb-2 text-validation-destructive">
          <FormattedMessage
            defaultMessage="Source ''{domain}'' already exists in this collection"
            id="XbJl/LtCzU"
            description="Error shown when a user attempts to add a source that is already present in the collection"
            values={{ domain }}
          />
        </TypographyCaption>
      )}
    </div>
  );
};
